import { useCallback, useEffect, useState } from 'react'
import {
  changerStatutLivreur,
  chargerDossierLivreur,
  debloquerQuiz,
  definirPermisRequis,
  enregistrerFormation,
  enregistrerIdentiteLivreur,
  rejeterPiece,
  validerPiece,
} from '../../api/candidatures'
import { CANEVAS_ACTIF } from '../../onboarding/canevas'
import { signatureDocumensoEnCours } from '../../onboarding/contrats'
import { actionsStatut, etapesLivreur, prochaineActionStaff, statutAvantArret } from '../../onboarding/etapes'
import { messageErreur } from '../../onboarding/erreurs'
import { manquantsDeLaSaisie, saisieDepuis } from '../../onboarding/identite'
import { piecesManquantes } from '../../onboarding/pieces'
import { etatQuiz } from '../../onboarding/quiz'
import {
  LIBELLE_DOC_LIVREUR,
  PIECES_LIVREUR,
  type CandidatureLivreur,
  type DossierLivreur,
} from '../../onboarding/types'
import { dateCourte, dateComplete } from '../Format'
import { Ligne, Modale } from '../Modale'
import {
  Bloc,
  BadgeStatut,
  BlocDecision,
  Erreur,
  Info,
  Journal,
  ProchaineAction,
  Stepper,
  useAction,
} from './Briques'
import { BlocIdentite } from './BlocIdentite'
import { EntretienEcran } from './EntretienEcran'
import { GrillePapier } from './GrillePapier'
import { ListePieces } from './ListePieces'
import { SectionContrat } from './SectionContrat'

const LIBELLE_RECO = { retenir: 'Retenir', revoir: 'À revoir', refuser: 'Refuser' } as const
const COULEUR_RECO = { retenir: 'green', revoir: 'yellow', refuser: 'red' } as const

const aujourdhui = () => new Date().toISOString().slice(0, 10)

/** Fiche dossier d'un candidat livreur (contenu du panneau latéral large). */
export function FicheLivreur({ cand, surChange }: { cand: CandidatureLivreur; surChange: () => void }) {
  const [dossier, setDossier] = useState<DossierLivreur | null>(null)
  const [erreurLecture, setErreurLecture] = useState<string | null>(null)
  const [permis, setPermis] = useState(cand.permisRequis)
  const [entretien, setEntretien] = useState(false)
  const [papier, setPapier] = useState(false)

  const recharger = useCallback(async () => {
    try {
      setDossier(await chargerDossierLivreur(cand.id))
      setErreurLecture(null)
    } catch (e) {
      setErreurLecture(messageErreur(e))
    }
  }, [cand.id])

  useEffect(() => {
    setDossier(null)
    void recharger()
  }, [recharger])

  useEffect(() => setPermis(cand.permisRequis), [cand.permisRequis])

  /** Après toute écriture : recharger le dossier ET la liste (le statut a pu avancer tout seul). */
  const apres = useCallback(async () => {
    await recharger()
    surChange()
  }, [recharger, surChange])

  if (erreurLecture) return <Erreur texte={erreurLecture} />
  if (!dossier) return <p className="dos-note">Chargement du dossier…</p>

  const requises = permis ? [...PIECES_LIVREUR, 'drivers_license'] : [...PIECES_LIVREUR]
  const pieces = dossier.pieces
  const quiz = etatQuiz(dossier.tentatives, dossier.quizDeblocageLe)
  const identiteManquante = manquantsDeLaSaisie('livreur', saisieDepuis(dossier.identite))
  const action = prochaineActionStaff('livreur', cand.statut, {
    identiteManquante: identiteManquante.length,
    piecesEnAttente: pieces.filter((p) => p.type !== 'signed_contract' && p.statut === 'en_attente').length,
    piecesManquantes: piecesManquantes(requises, pieces).length,
    quizBloque: quiz.bloque,
    signatureEnCours: signatureDocumensoEnCours(dossier.signatures) !== null,
  })

  return (
    <>
      <Bloc titre="Candidat" droite={<BadgeStatut statut={cand.statut} />}>
        <Ligne libelle="Nom">{cand.nom}</Ligne>
        <Ligne libelle="Téléphone">{cand.telephone || '—'}</Ligne>
        <Ligne libelle="E-mail">{cand.email || '—'}</Ligne>
        <Ligne libelle="Véhicule">{cand.vehicule || '—'}</Ligne>
        <Ligne libelle="Candidature">{dateCourte(cand.creeLe)}</Ligne>
        {cand.motif && (cand.statut === 'refuse' || cand.statut === 'suspendu') && (
          <Ligne libelle="Motif">{cand.motif}</Ligne>
        )}
      </Bloc>

      <Bloc titre="Parcours">
        <Stepper etapes={etapesLivreur(cand.statut, statutAvantArret(dossier.journal))} />
        <ProchaineAction action={action} />
      </Bloc>

      <Bloc titre="Statut juridique et identité">
        <BlocIdentite
          sujet="livreur"
          ligne={dossier.identite}
          surEnregistrer={async (saisie) => {
            const r = await enregistrerIdentiteLivreur(cand.id, saisie)
            await apres()
            return r
          }}
        />
        <p className="dos-note" style={{ marginTop: 6 }}>
          Le livreur peut aussi la saisir lui-même dans « Mon inscription » de son application. Elle est exigée avant
          la signature du contrat.
        </p>
      </Bloc>

      <Bloc titre="Pièces du dossier">
        <PermisRequis
          coche={permis}
          surChange={async (v) => {
            await definirPermisRequis(cand.id, v)
            setPermis(v)
            await apres()
          }}
        />
        <ListePieces
          pieces={pieces}
          requises={requises}
          libelles={LIBELLE_DOC_LIVREUR}
          avertirValidite
          surValider={async (p) => {
            await validerPiece('driver_documents', p.id)
            await apres()
          }}
          surRejeter={async (p, motif) => {
            await rejeterPiece('driver_documents', p.id, motif)
            await apres()
          }}
        />
      </Bloc>

      <Bloc titre="Entretien">
        {dossier.entretiens.length === 0 ? (
          <p className="dos-note">Aucun entretien enregistré.</p>
        ) : (
          <ul className="dos-liste">
            {dossier.entretiens.map((e) => (
              <li key={e.id}>
                <span className={`badge badge-${COULEUR_RECO[e.recommandation]}`}>{LIBELLE_RECO[e.recommandation]}</span>
                <span>Score {e.score.toFixed(2).replace('.', ',')} / 5</span>
                {e.elimine && <span className="badge badge-red">Éliminatoire</span>}
                {e.notes && <span className="dos-note">« {e.notes} »</span>}
                <span className="quand">canevas v{e.version} · {dateComplete(e.date)}</span>
              </li>
            ))}
          </ul>
        )}
        <div className="dos-champs">
          <button className="btn btn-accent btn-sm" onClick={() => setEntretien(true)}>Mener l'entretien</button>
          <button className="btn btn-outline btn-sm" onClick={() => setPapier(true)}>Imprimer la grille</button>
        </div>
        {cand.statut === 'candidature' && (
          <p className="dos-note" style={{ marginTop: 6 }}>
            Le dossier n'est pas encore complet : un entretien « retenir » ne fera avancer le statut qu'à partir de « Dossier complet ».
          </p>
        )}
      </Bloc>

      <Bloc titre="Formation">
        <Formation formations={dossier.formations} statut={cand.statut} driverId={cand.id} surChange={apres} />
      </Bloc>

      <Bloc titre="Quiz">
        <QuizEtat
          etat={quiz}
          tentatives={dossier.tentatives}
          statut={cand.statut}
          surDebloquer={async () => {
            await debloquerQuiz(cand.id)
            await apres()
          }}
        />
      </Bloc>

      <Bloc titre="Contrat">
        <SectionContrat
          kind="livreur"
          signerId={cand.id}
          nomSignataire={cand.nom}
          telephone={cand.telephone}
          email={cand.email}
          signatures={dossier.signatures}
          surChange={() => void apres()}
        />
      </Bloc>

      <Bloc titre="Décision">
        <BlocDecision
          actions={actionsStatut('livreur', cand.statut)}
          surDecider={async (vers, motif) => {
            await changerStatutLivreur(cand.id, vers, motif)
            await apres()
          }}
        />
      </Bloc>

      <Bloc titre="Journal du dossier">
        <Journal evenements={dossier.journal} />
      </Bloc>

      <Modale titre={`Entretien — ${cand.nom}`} ouverte={entretien} large surFermer={() => setEntretien(false)}>
        {entretien && (
          <EntretienEcran
            driverId={cand.id}
            nom={cand.nom}
            surEnregistre={() => {
              setEntretien(false)
              void apres()
            }}
          />
        )}
      </Modale>
      {papier && <GrillePapier canevas={CANEVAS_ACTIF} surFermer={() => setPapier(false)} />}
    </>
  )
}

function PermisRequis({ coche, surChange }: { coche: boolean; surChange: (v: boolean) => Promise<void> }) {
  const { occupe, erreur, lancer } = useAction()
  return (
    <div style={{ marginBottom: 6 }}>
      <label className="sous-liens" style={{ fontSize: 12.5 }}>
        <input
          type="checkbox"
          checked={coche}
          disabled={occupe}
          onChange={(e) => void lancer(() => surChange(e.target.checked))}
        />
        Permis de conduire exigé (selon la cylindrée)
      </label>
      <Erreur texte={erreur} />
    </div>
  )
}

function Formation({
  formations,
  statut,
  driverId,
  surChange,
}: {
  formations: DossierLivreur['formations']
  statut: string
  driverId: string
  surChange: () => Promise<void>
}) {
  const [date, setDate] = useState(aujourdhui())
  const [formateur, setFormateur] = useState('')
  const [notes, setNotes] = useState('')
  const { occupe, erreur, info, lancer } = useAction()

  return (
    <>
      {formations.length > 0 && (
        <ul className="dos-liste">
          {formations.map((f) => (
            <li key={f.id}>
              <b>{dateCourte(f.date)}</b>
              <span>{f.formateur}</span>
              {f.notes && <span className="dos-note">« {f.notes} »</span>}
            </li>
          ))}
        </ul>
      )}
      <div className="dos-champs">
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} aria-label="Date de la session" />
        <input className="grow" placeholder="Formateur" value={formateur} onChange={(e) => setFormateur(e.target.value)} />
      </div>
      <div className="dos-champs">
        <input className="grow" placeholder="Notes (facultatif)" value={notes} onChange={(e) => setNotes(e.target.value)} />
        <button
          className="btn btn-accent btn-sm"
          disabled={occupe || !date}
          onClick={() =>
            void lancer(async () => {
              await enregistrerFormation(driverId, date, formateur, notes)
              setFormateur('')
              setNotes('')
              await surChange()
              return 'Formation enregistrée.'
            })
          }
        >
          Enregistrer la formation
        </button>
      </div>
      {statut !== 'entretien_fait' && (
        <p className="dos-note" style={{ marginTop: 6 }}>
          Le statut n'avance automatiquement qu'à partir de « Entretien fait ».
        </p>
      )}
      <Erreur texte={erreur} />
      <Info texte={info} />
    </>
  )
}

function QuizEtat({
  etat,
  tentatives,
  statut,
  surDebloquer,
}: {
  etat: ReturnType<typeof etatQuiz>
  tentatives: DossierLivreur['tentatives']
  statut: string
  surDebloquer: () => Promise<void>
}) {
  const { occupe, erreur, lancer } = useAction()
  return (
    <>
      <div className="sous-liens" style={{ fontSize: 13 }}>
        {etat.reussi ? (
          <span className="badge badge-green">Réussi</span>
        ) : etat.bloque ? (
          <span className="badge badge-red">Bloqué après 3 échecs</span>
        ) : statut === 'formation_faite' ? (
          <span className="badge badge-yellow">En cours — {etat.tentativesRestantes} essai(s) restant(s)</span>
        ) : (
          <span className="badge badge-gray">Pas encore accessible (après la formation)</span>
        )}
        {!etat.reussi && etat.echecsRecents > 0 && <span className="dos-note">{etat.echecsRecents} échec(s) depuis le dernier déblocage</span>}
      </div>
      {tentatives.length > 0 && (
        <ul className="dos-liste">
          {tentatives.map((t) => (
            <li key={t.id}>
              <span className={`badge badge-${t.reussi ? 'green' : 'red'}`}>{t.reussi ? 'Réussi' : 'Échec'}</span>
              <span>{t.bonnes}/{t.total} ({Math.round(t.scorePct)} %)</span>
              <span className="quand">{dateComplete(t.date)}</span>
            </li>
          ))}
        </ul>
      )}
      <div className="dos-champs">
        <button className="btn btn-outline btn-sm" disabled={!etat.bloque || occupe} onClick={() => void lancer(surDebloquer)}>
          Débloquer le quiz
        </button>
        {!etat.bloque && <span className="dos-note">Disponible après 3 échecs.</span>}
      </div>
      <Erreur texte={erreur} />
    </>
  )
}
